import type { TagPick } from '@/editor/TagTool';
import {
  DEFAULT_TAG_RADIUS_METERS,
  MAX_TAG_LABEL_LENGTH,
  MAX_TAG_NOTE_LENGTH,
  TAG_CATEGORY_INFO,
  TAG_EMPHASES,
  createSemanticTag,
  tagCategoryInfo,
  type SemanticTag,
  type TagCategory,
  type TagEmphasis,
  type TagTarget,
} from '@/semantics/SemanticTag';
import type { SemanticDescriptor, SemanticProfile } from '@/semantics/SemanticProfile';
import {
  TAG_IMAGE_BUDGET,
  TAG_IMAGE_INTENT_INFO,
  formatBytes,
  tagImageIntentInfo,
  type TagImage,
  type TagImageIntent,
} from '@/semantics/TagImage';
import { append, clear, el, setClass } from './dom';
import {
  IMAGE_ACCEPT_ATTRIBUTE,
  imagesFromClipboard,
  imagesFromDrop,
  intakeImages,
  pasteCarriesText,
} from './ImageIntake';
import { icon } from './Icons';

/**
 * The tag tool's editing surface: a floating panel anchored to whatever was clicked.
 *
 * ## Scope is a row of chips, not a mode
 *
 * One click on a shrub standing on gravel could reasonably mean four things — *this shrub*, *this
 * spot*, *gravel on this site*, or *the garden as a whole* — and which one the designer meant is not
 * knowable from the click. Asking first (a modal "what are you tagging?") would put a question before
 * every tag; guessing would be wrong a quarter of the time. So all four are offered as chips, the
 * most specific one is preselected, and switching between them is one click that swaps the list
 * beneath. The scope is visible at all times, which matters because a tag on the wrong scope is
 * invisible until a render comes back strange.
 *
 * ## Already known, and shown
 *
 * Above the editor sits everything the render already knows about this target without anyone typing:
 * the species' catalog tags, a prop's category and materials, the paving's character, the ground
 * layer's description. It is read-only and it is the answer to "do I need to say this?" — which is
 * the question that decides whether tagging is worth the designer's time at all. Hiding it would
 * leave someone typing "gravel" onto gravel.
 *
 * ## Written immediately, undoably
 *
 * A tag lands on the shared history the moment it is added, exactly as a planted tree does. There is
 * no draft state to lose and no Save button to forget; Ctrl+Z is the app's answer to a mistake and
 * this feature does not need a second one.
 */

export interface TagBubbleDeps {
  /** The HUD root, so the bubble inherits the theme and disappears in photo mode. */
  host: HTMLElement;
  /** Tags currently on a target, loudest first. */
  tagsFor: (target: TagTarget) => SemanticTag[];
  /** Everything already known about a target without a manual tag — the read-only half. */
  profileFor: (target: TagTarget) => SemanticProfile | null;
  /** Human name for a target, used in the header and the scope chips. */
  describeTarget: (target: TagTarget) => string;
  /** Which ground material is painted at a point, so the "this material" scope can be offered. */
  groundLayerAt: (x: number, z: number) => TagTarget | null;
  /** Commit a new tag through the shared history. */
  addTag: (tag: SemanticTag) => void;
  /** Patch an existing tag through the shared history. `mergeKey` collapses a drag or a burst of typing. */
  updateTag: (id: string, patch: Partial<SemanticTag>, label: string, mergeKey?: string) => void;
  removeTag: (id: string) => void;
  onClose?: () => void;
}

export class TagBubble {
  readonly root: HTMLElement;

  private readonly body: HTMLElement;
  private readonly headline: HTMLElement;

  private pick: TagPick | null = null;
  private scope: TagTarget | null = null;
  private draftCategory: TagCategory = 'identity';
  private draftEmphasis: TagEmphasis = 'mention';
  private draftLabel = '';
  private draftNote = '';
  private draftImages: TagImage[] = [];
  private intakeError = '';
  /** Where the panel was opened, kept so it can be re-clamped once its real height is known. */
  private anchor: { x: number; y: number } | null = null;
  private showKnown = true;
  private readonly fileInput: HTMLInputElement;

  /**
   * True while the radius slider is being dragged.
   *
   * Each `input` event writes a tag through the history, which loops back here as a store change —
   * and re-rendering mid-drag tears the slider out of the DOM, so the drag dies after one frame. The
   * inspector's sliders solved this the same way; rebuilds are deferred to pointerup.
   */
  private dragging = false;
  private pendingRefresh = false;
  private readonly onPointerUp = (): void => {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.pendingRefresh) this.refresh();
  };

  constructor(private readonly deps: TagBubbleDeps) {
    this.headline = el('span', { class: 'lg-tagger__headline' });
    this.body = el('div', { class: 'lg-tagger__body' });

    this.fileInput = el('input', {
      class: 'lg-tagger__file',
      attrs: {
        type: 'file',
        accept: IMAGE_ACCEPT_ATTRIBUTE,
        multiple: true,
        'aria-label': 'Attach a reference photograph',
      },
      on: { change: () => void this.adoptFiles() },
    });

    this.root = el(
      'div',
      { class: 'lg-tagger', attrs: { role: 'dialog', 'aria-label': 'Semantic tags' } },
      el(
        'div',
        { class: 'lg-tagger__head' },
        el('span', { class: 'lg-tagger__mark', html: icon('tag', 16) }),
        el(
          'div',
          { class: 'lg-tagger__heading' },
          el('span', { class: 'lg-tagger__title', text: 'Tag' }),
          this.headline,
        ),
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('close', 15),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the tag panel' },
          on: { click: () => this.close() },
        }),
      ),
      this.body,
      this.fileInput,
    );

    // Paste and drop, on the same reasoning as the wand's: a reference is nearly always on the
    // clipboard and nowhere else, and making someone save a file first is a detour around a panel
    // whose whole point is to be quick. Text pastes into the label or note fields are left alone.
    this.root.addEventListener('paste', (event) => {
      const files = imagesFromClipboard(event);
      if (files.length === 0) return;
      const target = document.activeElement;
      const typing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
      if (pasteCarriesText(event) && typing) return;
      event.preventDefault();
      event.stopPropagation();
      void this.addImages(files);
    });
    this.root.addEventListener('dragover', (event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      event.stopPropagation();
      setClass(this.root, 'is-dropping', true);
    });
    this.root.addEventListener('dragleave', (event) => {
      if (event.target !== this.root) return;
      setClass(this.root, 'is-dropping', false);
    });
    this.root.addEventListener('drop', (event) => {
      setClass(this.root, 'is-dropping', false);
      const files = imagesFromDrop(event);
      if (files.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      void this.addImages(files);
    });

    // The editor listens on `window`; a stray `P` typed into the label box must not arm planting.
    this.root.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });
    this.root.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      if ((event.target as HTMLElement | null)?.closest('.lg-slider')) this.dragging = true;
    });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  get isOpen(): boolean {
    return this.root.isConnected;
  }

  /** Open against a freshly clicked target. Any half-typed draft is dropped — this is a new thing. */
  open(pick: TagPick, screen: { x: number; y: number }): void {
    this.pick = pick;
    this.scope = pick.target;
    this.draftLabel = '';
    this.draftNote = '';
    this.draftEmphasis = 'mention';
    this.draftCategory = defaultCategoryFor(pick.target);
    if (!this.root.isConnected) this.deps.host.appendChild(this.root);
    this.position(screen);
    this.render();
    this.focusLabel();
  }

  close(): void {
    this.root.remove();
    this.pick = null;
    this.scope = null;
    this.deps.onClose?.();
  }

  /** Redraw against the current store — called after an add, edit, or undo lands. */
  refresh(): void {
    if (!this.isOpen) return;
    if (this.dragging) {
      this.pendingRefresh = true;
      return;
    }
    this.pendingRefresh = false;
    this.render();
  }

  dispose(): void {
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.root.remove();
  }

  /* --------------------------------- Placement --------------------------------- */

  /** Beside the click rather than over it, so the thing being described stays visible. */
  private position(screen: { x: number; y: number }): void {
    const width = 340;
    const margin = 12;
    this.anchor = screen;
    this.root.style.left = `${Math.min(
      Math.max(margin, screen.x + 20),
      Math.max(margin, window.innerWidth - width - margin),
    )}px`;
    this.root.style.top = `${Math.min(Math.max(64, screen.y - 40), Math.max(64, window.innerHeight - 300))}px`;
  }

  /**
   * Pull the panel back inside the window once its real height is known.
   *
   * {@link position} runs before the body is built, so it can only guess — it assumed about 300 px,
   * which stopped being true the moment a tag could carry thumbnails: the Add button ended up below
   * the fold, on a panel whose whole job is to be typed into. Measuring after the fact costs one
   * layout read on an interaction that already rebuilt the DOM.
   */
  private clampIntoView(): void {
    if (!this.root.isConnected || !this.anchor) return;
    const margin = 12;
    const height = this.root.offsetHeight;
    if (height === 0) return;
    const top = Math.min(
      Math.max(64, this.anchor.y - 40),
      Math.max(64, window.innerHeight - height - margin),
    );
    this.root.style.top = `${top}px`;
  }

  /* ---------------------------------- Render ---------------------------------- */

  private render(): void {
    const scope = this.scope;
    if (!scope) return;

    this.headline.textContent = this.deps.describeTarget(scope);
    clear(this.body);

    append(
      this.body,
      this.scopeRow(),
      this.knownBlock(),
      this.tagList(),
      this.radiusControl(),
      this.editor(),
    );

    this.clampIntoView();
  }

  /**
   * The four scopes this click could have meant.
   *
   * Built from the pick rather than from a fixed list: a click on bare ground offers no plant chip,
   * and unpainted ground offers no material chip, because a scope that would produce a tag about
   * nothing is worse than one absence.
   */
  private scopeRow(): HTMLElement {
    const row = el('div', { class: 'lg-tagger__scopes' });
    const pick = this.pick;
    if (!pick) return row;

    const scopes: TagTarget[] = [];
    if (pick.target.kind !== 'location') scopes.push(pick.target);
    scopes.push({
      kind: 'location',
      x: pick.point.x,
      z: pick.point.z,
      radiusMeters: DEFAULT_TAG_RADIUS_METERS,
    });
    const ground = this.deps.groundLayerAt(pick.point.x, pick.point.z);
    if (ground) scopes.push(ground);
    scopes.push({ kind: 'site' });

    append(row, el('span', { class: 'lg-tagger__scopelabel', text: 'Tagging' }));
    for (const target of scopes) {
      const active = this.scope !== null && sameScope(target, this.scope);
      const count = this.deps.tagsFor(target).length;
      row.appendChild(
        el('button', {
          class: `lg-tagger__scope${active ? ' is-active' : ''}`,
          text: count > 0 ? `${this.deps.describeTarget(target)} · ${count}` : this.deps.describeTarget(target),
          title: SCOPE_HINT[target.kind],
          attrs: { type: 'button', 'aria-pressed': active },
          on: {
            click: () => {
              // Keep the location scope's radius across a switch away and back — it is a choice the
              // user made about this spot, not a property of which chip is lit.
              this.scope = this.scope !== null && sameScope(target, this.scope) ? this.scope : target;
              this.draftCategory = defaultCategoryFor(target);
              this.render();
              this.focusLabel();
            },
          },
        }),
      );
    }
    return row;
  }

  /** What the render already knows, with no tag typed. Collapsible, because it is reference. */
  private knownBlock(): HTMLElement | null {
    const scope = this.scope;
    if (!scope) return null;
    const profile = this.deps.profileFor(scope);
    const implicit = profile?.descriptors.filter((d) => d.source !== 'manual') ?? [];
    if (implicit.length === 0) return null;

    const block = el('div', { class: 'lg-tagger__known' });
    block.appendChild(
      el('button', {
        class: 'lg-tagger__knownhead',
        attrs: { type: 'button', 'aria-expanded': this.showKnown },
        html: `${icon('info', 13)}<span>Already known · ${implicit.length}</span>`,
        title:
          'Facts the render prompt carries for this target without any tag — from the plant catalog, ' +
          'the prop library, the paving record, or the ground material. Nothing here needs retyping.',
        on: {
          click: () => {
            this.showKnown = !this.showKnown;
            this.render();
          },
        },
      }),
    );
    if (this.showKnown) {
      const list = el('div', { class: 'lg-tagger__knownlist' });
      for (const descriptor of implicit) list.appendChild(this.descriptorChip(descriptor, false));
      block.appendChild(list);
    }
    return block;
  }

  private descriptorChip(descriptor: SemanticDescriptor, removable: boolean): HTMLElement {
    const info = tagCategoryInfo(descriptor.category);
    const chip = el(
      'span',
      {
        class: `lg-tagchip lg-tagchip--${descriptor.emphasis}${removable ? '' : ' is-implicit'}`,
        // Custom properties are set as an attribute: `Object.assign` on a CSSStyleDeclaration silently
        // drops `--x` names, so the `style` option cannot carry one.
        attrs: { style: `--tag-color:${info.color}` },
        title: descriptor.note !== undefined ? `${info.name} · ${descriptor.note}` : info.name,
      },
      el('span', { class: 'lg-tagchip__dot' }),
      el('span', { class: 'lg-tagchip__text', text: descriptor.label }),
    );
    if (removable && descriptor.tagId !== undefined) {
      const tagId = descriptor.tagId;
      chip.appendChild(
        el('button', {
          class: 'lg-tagchip__x',
          html: icon('close', 11),
          title: 'Remove this tag',
          attrs: { type: 'button', 'aria-label': `Remove ${descriptor.label}` },
          on: { click: () => this.deps.removeTag(tagId) },
        }),
      );
    }
    return chip;
  }

  /** The tags actually on this scope, each removable and each with an emphasis control. */
  private tagList(): HTMLElement | null {
    const scope = this.scope;
    if (!scope) return null;
    const tags = this.deps.tagsFor(scope);
    if (tags.length === 0) return null;

    const list = el('div', { class: 'lg-tagger__tags' });
    for (const tag of tags) {
      const info = tagCategoryInfo(tag.category);
      list.appendChild(
        el(
          'div',
          { class: 'lg-tagger__tag', attrs: { style: `--tag-color:${info.color}` } },
          el(
            'div',
            { class: 'lg-tagger__tagmain' },
            el('span', { class: 'lg-tagger__tagcat', text: info.name }),
            el('span', { class: 'lg-tagger__taglabel', text: tag.label }),
            tag.note !== undefined ? el('span', { class: 'lg-tagger__tagnote', text: tag.note }) : null,
            this.savedPictures(tag),
          ),
          this.emphasisRow(tag),
          el('button', {
            class: 'lg-tagchip__x',
            html: icon('close', 12),
            title: 'Remove this tag',
            attrs: { type: 'button', 'aria-label': `Remove ${tag.label}` },
            on: { click: () => this.deps.removeTag(tag.id) },
          }),
        ),
      );
    }
    return list;
  }

  /**
   * Thumbnails on a tag that has already been saved.
   *
   * Shown rather than merely counted, because the question this answers is "is *that* the picture
   * the render is following?" — and a line saying "1 picture" cannot answer it. Clicking one takes
   * it off the tag: a wrong reference is worse than none, and it should not need a re-typed tag to
   * remove.
   */
  private savedPictures(tag: SemanticTag): HTMLElement | null {
    const images = tag.images ?? [];
    if (images.length === 0) return null;

    const row = el('div', { class: 'lg-tagger__tagpics' });
    for (const image of images) {
      row.appendChild(
        el('button', {
          class: 'lg-tagger__tagpic',
          attrs: {
            type: 'button',
            title:
              `${tagImageIntentInfo(image.intent).name} — ${tagImageIntentInfo(image.intent).hint}\n` +
              `${image.widthPx}×${image.heightPx}, ${formatBytes(image.bytes)}\n\nClick to remove.`,
            'aria-label': `Remove the ${tagImageIntentInfo(image.intent).name} picture`,
            style: `background-image:url(${image.dataUrl})`,
          },
          on: {
            click: () => {
              this.deps.updateTag(
                tag.id,
                { images: images.filter((entry) => entry.id !== image.id) },
                'Remove tag picture',
              );
            },
          },
        }),
      );
    }
    return row;
  }

  private emphasisRow(tag: SemanticTag): HTMLElement {
    const row = el('div', { class: 'lg-tagger__emphasis' });
    for (const emphasis of TAG_EMPHASES) {
      row.appendChild(
        el('button', {
          class: `lg-tagger__level${tag.emphasis === emphasis ? ' is-active' : ''}`,
          text: EMPHASIS_LABEL[emphasis],
          title: EMPHASIS_HINT[emphasis],
          attrs: { type: 'button', 'aria-pressed': tag.emphasis === emphasis },
          on: { click: () => this.deps.updateTag(tag.id, { emphasis }, 'Set tag emphasis') },
        }),
      );
    }
    return row;
  }

  /**
   * How far a location tag reaches.
   *
   * Only shown for a location scope, and it edits the *draft* until a tag exists — a radius is a
   * property of the area being described, so every tag dropped at this spot shares it and changing
   * it moves them together.
   */
  private radiusControl(): HTMLElement | null {
    const scope = this.scope;
    if (!scope || scope.kind !== 'location') return null;
    const existing = this.deps.tagsFor(scope);

    const readout = el('span', { class: 'lg-field__value', text: `${scope.radiusMeters.toFixed(1)} m` });
    const input = el('input', {
      class: 'lg-slider',
      attrs: {
        type: 'range',
        min: '0.5',
        max: '30',
        step: '0.5',
        value: String(scope.radiusMeters),
        'aria-label': 'Area covered by this tag',
      },
      on: {
        input: (event) => {
          const radiusMeters = Number((event.target as HTMLInputElement).value);
          readout.textContent = `${radiusMeters.toFixed(1)} m`;
          this.scope = { ...scope, radiusMeters };
          for (const tag of existing) {
            this.deps.updateTag(
              tag.id,
              { target: { ...scope, radiusMeters } },
              'Resize tagged area',
              `tag-radius-${tag.id}`,
            );
          }
        },
      },
    });

    return el(
      'div',
      { class: 'lg-control lg-tagger__radius' },
      el(
        'div',
        { class: 'lg-control__head' },
        el('span', { class: 'lg-field__label', text: 'Area' }),
        readout,
      ),
      input,
    );
  }

  /* ---------------------------------- Editor ---------------------------------- */

  private editor(): HTMLElement {
    const categories = el('div', { class: 'lg-tagger__cats' });
    for (const info of TAG_CATEGORY_INFO) {
      categories.appendChild(
        el('button', {
          class: `lg-tagger__cat${this.draftCategory === info.id ? ' is-active' : ''}`,
          text: info.name,
          title: `${info.hint}\n\ne.g. “${info.example}”`,
          attrs: {
            type: 'button',
            'aria-pressed': this.draftCategory === info.id,
            style: `--tag-color:${info.color}`,
          },
          on: {
            click: () => {
              this.draftCategory = info.id;
              this.render();
              this.focusLabel();
            },
          },
        }),
      );
    }

    const label = el('input', {
      class: 'lg-input lg-tagger__label',
      attrs: {
        type: 'text',
        value: this.draftLabel,
        maxlength: String(MAX_TAG_LABEL_LENGTH),
        placeholder: tagCategoryInfo(this.draftCategory).example,
        'aria-label': 'Tag',
        spellcheck: 'true',
      },
      on: {
        input: (event) => {
          this.draftLabel = (event.target as HTMLInputElement).value;
        },
        keydown: (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.commit();
          }
        },
      },
    });

    const note = el('input', {
      class: 'lg-input lg-tagger__note',
      attrs: {
        type: 'text',
        value: this.draftNote,
        maxlength: String(MAX_TAG_NOTE_LENGTH),
        placeholder: 'Optional detail for the render…',
        'aria-label': 'Tag detail',
        spellcheck: 'true',
      },
      on: {
        input: (event) => {
          this.draftNote = (event.target as HTMLInputElement).value;
        },
        keydown: (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.commit();
          }
        },
      },
    });

    const emphasis = el('div', { class: 'lg-tagger__emphasis' });
    for (const level of TAG_EMPHASES) {
      emphasis.appendChild(
        el('button', {
          class: `lg-tagger__level${this.draftEmphasis === level ? ' is-active' : ''}`,
          text: EMPHASIS_LABEL[level],
          title: EMPHASIS_HINT[level],
          attrs: { type: 'button', 'aria-pressed': this.draftEmphasis === level },
          on: {
            click: () => {
              this.draftEmphasis = level;
              this.render();
              this.focusLabel();
            },
          },
        }),
      );
    }

    const add = el(
      'button',
      {
        class: 'lg-button lg-button--primary',
        attrs: { type: 'button' },
        title: 'Add this tag  ·  Enter',
        on: { click: () => this.commit() },
      },
      el('span', { text: 'Add tag' }),
    );

    return el(
      'div',
      { class: 'lg-tagger__editor' },
      categories,
      label,
      note,
      this.pictureRow(),
      el('div', { class: 'lg-tagger__foot' }, emphasis, el('span', { class: 'lg-tagger__spacer' }), add),
    );
  }

  /**
   * Pictures on the tag being written.
   *
   * This is the half of a tag that words cannot carry. "Split-rail cedar fence" still leaves the
   * rail count, the post spacing and the joint to be invented; a photograph settles all three at
   * once. The intent picker beside each thumbnail is what stops the picture being dangerous — an
   * unexplained reference gets its background and its light composited into the frame, so every
   * image states whether it governs the object, only its surface, or only the register.
   */
  private pictureRow(): HTMLElement {
    const row = el('div', { class: 'lg-tagger__pictures' });

    for (const [index, image] of this.draftImages.entries()) {
      const intent = tagImageIntentInfo(image.intent);
      row.appendChild(
        el(
          'div',
          { class: 'lg-tagger__picture', attrs: { title: `${image.widthPx}×${image.heightPx}, ${formatBytes(image.bytes)}` } },
          el('img', { attrs: { src: image.dataUrl, alt: image.name ?? 'Attached reference' } }),
          el('select', {
            class: 'lg-select lg-tagger__intent',
            attrs: { 'aria-label': 'How this picture should be read' },
            on: {
              change: (event) => {
                const value = (event.target as HTMLSelectElement).value as TagImageIntent;
                const current = this.draftImages[index];
                if (current) this.draftImages[index] = { ...current, intent: value };
                this.render();
              },
            },
          },
          ...TAG_IMAGE_INTENT_INFO.map((info) =>
            el('option', {
              text: info.name,
              title: info.hint,
              attrs: { value: info.id, selected: info.id === image.intent },
            }),
          )),
          el('button', {
            class: 'lg-tagger__pictureremove',
            html: icon('close', 12),
            title: `${intent.hint}\n\nRemove this picture.`,
            attrs: { type: 'button', 'aria-label': 'Remove this picture' },
            on: {
              click: () => {
                this.draftImages.splice(index, 1);
                this.render();
              },
            },
          }),
        ),
      );
    }

    if (this.draftImages.length < TAG_IMAGE_BUDGET.perTag) {
      row.appendChild(
        el(
          'button',
          {
            class: 'lg-tagger__addpicture',
            title:
              'Attach a photograph of how this should look.\n' +
              'It is stored with the site and sent with every render, bound to this thing alone.\n\n' +
              'You can also paste or drop a picture anywhere in this panel.',
            attrs: { type: 'button' },
            on: { click: () => this.fileInput.click() },
          },
          el('span', { html: icon('gallery', 14) }),
          el('span', { text: this.draftImages.length === 0 ? 'Add a picture — or paste one' : 'Add another' }),
        ),
      );
    }

    if (this.intakeError) {
      row.appendChild(el('span', { class: 'lg-tagger__pictureerror', text: this.intakeError }));
    }

    return row;
  }

  private async adoptFiles(): Promise<void> {
    const files = [...(this.fileInput.files ?? [])];
    this.fileInput.value = '';
    await this.addImages(files);
  }

  private async addImages(files: readonly File[]): Promise<void> {
    const room = TAG_IMAGE_BUDGET.perTag - this.draftImages.length;
    if (room <= 0) {
      this.intakeError = `A tag carries ${TAG_IMAGE_BUDGET.perTag} pictures.`;
      this.render();
      return;
    }
    const { images, rejected } = await intakeImages(files, { limit: room });
    this.draftImages = [...this.draftImages, ...images];
    this.intakeError = rejected.length > 0 ? (rejected[0] as string) : '';
    this.render();
  }

  private commit(): void {
    const scope = this.scope;
    if (!scope) return;
    if (this.draftLabel.trim().length === 0) {
      this.focusLabel();
      return;
    }
    this.deps.addTag(
      createSemanticTag({
        target: scope,
        label: this.draftLabel,
        category: this.draftCategory,
        emphasis: this.draftEmphasis,
        ...(this.draftNote.trim().length > 0 ? { note: this.draftNote } : {}),
        ...(this.draftImages.length > 0 ? { images: this.draftImages } : {}),
      }),
    );
    this.draftLabel = '';
    this.draftNote = '';
    this.draftImages = [];
    this.intakeError = '';
    this.render();
    this.focusLabel();
  }

  private focusLabel(): void {
    const field = this.root.querySelector<HTMLInputElement>('.lg-tagger__label');
    field?.focus();
  }
}

/** Two scopes are the same when they point at the same thing — a location's radius is not identity. */
function sameScope(a: TagTarget, b: TagTarget): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'plant':
      return b.kind === 'plant' && a.instanceId === b.instanceId;
    case 'structure':
      return b.kind === 'structure' && a.objectId === b.objectId;
    case 'ground':
      return b.kind === 'ground' && a.layer === b.layer;
    case 'site':
      return true;
    case 'location':
      return b.kind === 'location' && a.x === b.x && a.z === b.z;
  }
}

/**
 * The category a scope most likely wants first.
 *
 * A place is almost always tagged for what it is *for*; a thing for what it *is*; a material for what
 * it is *made of*; the site for its *style*. Getting this right saves a click on the common case and
 * costs nothing on the rest, since every category stays one click away.
 */
function defaultCategoryFor(target: TagTarget): TagCategory {
  switch (target.kind) {
    case 'location':
      return 'use';
    case 'ground':
      return 'material';
    case 'site':
      return 'style';
    default:
      return 'identity';
  }
}

const SCOPE_HINT: Record<TagTarget['kind'], string> = {
  plant: 'This one plant. The tag travels with it and is described where it stands in frame.',
  structure: 'This building, paved area, or prop.',
  location: 'A circle of ground. Anything standing inside it is described as being there.',
  ground: 'Every patch of this painted material on the site, not just this one.',
  site: 'The whole garden. Use for the register the entire render should sit inside.',
};

const EMPHASIS_LABEL: Record<TagEmphasis, string> = {
  mention: 'Mention',
  emphasise: 'Emphasise',
  critical: 'Essential',
};

const EMPHASIS_HINT: Record<TagEmphasis, string> = {
  mention: 'Stated once, in passing.',
  emphasise: 'Called out as something the render must make clearly readable.',
  critical: 'Named as essential — the render is wrong without it.',
};
